import { Button, Card, Stack, Text } from '@etg/wings';
import { Pictogram } from '@eti/components';
import { useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { Link } from 'react-router-dom';
import { SEAT_MAP } from '@eti/products';
import { TripType } from '@eti/schema-types';
import { bulletStyles } from '../../../../../utils/styleUtils';
import Banner from '../../../../../common/components/Banner';
import { routes } from '../../../../../constants/routesAndApisConstants';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import BoundSeatMap from './BoundSeatMap';
import type { IncludedProductType } from './utils';

interface SeatDrawerProps {
  product: IncludedProductType;
}

const pictogramStyles = css`
  align-self: center;
  margin: 0 16px;
`;

const SeatDrawer = ({ product }: SeatDrawerProps) => {
  const { brand } = useSiteContext();
  const { t } = useTranslation();
  const { availableExtraProducts, bounds, journeyType } = useOrderDetails();

  const seatExtraProduct = availableExtraProducts?.find(
    (availableExtraProduct) => availableExtraProduct.id === SEAT_MAP.id.toString(),
  );

  const shouldBeRenderedAsList = journeyType !== TripType.OneWay;

  return (
    <Stack>
      <Card
        data-testid="included-product-seat-map-overlay"
        role={shouldBeRenderedAsList ? 'list' : undefined}
      >
        {bounds.map(
          (bound, index) =>
            product.buySpecification &&
            product.buySpecification.__typename === 'BuySpecificationCartTravelerSeatMap' && (
              <Card.Section
                key={`bound-seat-map-${bound.boundId}`}
                role={shouldBeRenderedAsList ? 'listitem' : undefined}
              >
                <BoundSeatMap
                  bound={bound}
                  boundIndex={index}
                  travelerSeatMapPrices={product.buySpecification.travelerSeatMapPrices}
                />
              </Card.Section>
            ),
        )}
      </Card>

      {seatExtraProduct && (
        <Banner
          data-testid="seating"
          description={seatExtraProduct.texts.salesAbstract ?? ''}
          footer={
            <Button
              as={Link}
              data-testid="seating-buy-button"
              to={`${routes.ORDER_DETAILS_EXTRA_PRODUCTS}#product-id-${SEAT_MAP.id}`}
            >
              {t('OrderDetails.WhatIsIncluded.NotIncluded.Buy.Button.Label')}
            </Button>
          }
          headingAs="h3"
          icon={
            <Pictogram
              brand={brand.code}
              className={pictogramStyles}
              data-testid="seating-banner-icon"
              id="seating"
              size="5rem"
            />
          }
          title={seatExtraProduct.name}
        />
      )}
      <Card data-testid="included-product-seating-overlay-description">
        <Text asHtml className={bulletStyles()}>
          {product.description}
        </Text>
      </Card>
    </Stack>
  );
};

export default SeatDrawer;
